import React, { useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { PasswordInput } from './PasswordInput'
import { Mail, Loader2, AlertCircle } from 'lucide-react'
import type { SignInData } from '../../types/auth'

interface SignInFormProps {
  onSwitchToSignUp: () => void
}

export const SignInForm: React.FC<SignInFormProps> = ({ onSwitchToSignUp }) => {
  const { signIn, loading, error, clearError } = useAuth()
  const [formData, setFormData] = useState<SignInData>({
    email: '',
    password: ''
  })
  const [formErrors, setFormErrors] = useState<Partial<SignInData>>({})

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    
    // Clear field error when user starts typing
    if (formErrors[name as keyof SignInData]) {
      setFormErrors(prev => ({ ...prev, [name]: undefined }))
    }
    
    // Clear auth error when user starts typing
    if (error) {
      clearError()
    }
  }

  const validateForm = (): boolean => {
    const errors: Partial<SignInData> = {}

    if (!formData.email.trim()) {
      errors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address'
    }

    if (!formData.password.trim()) {
      errors.password = 'Password is required'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    const { error } = await signIn(formData)
    
    if (error) {
      console.error('Sign in error:', error)
    } else {
      // Successful login - redirect to chat
      window.location.href = '/chat'
    }
  }

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="
        bg-gradient-to-br from-dark-secondary/80 to-dark-tertiary/60
        backdrop-blur-lg 
        border border-dark-tertiary/30 
        rounded-2xl 
        p-8 
        shadow-xl
      ">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-light-primary mb-2">
            Welcome Back
          </h2>
          <p className="text-light-tertiary">
            Sign in to access your BLP Assistant
          </p>
        </div>

        {error && (
          <div className="
            mb-6 p-4 
            bg-red-500/10 
            border border-red-500/30 
            rounded-lg 
            flex items-center space-x-3
          ">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-light-secondary mb-2">
              Email Address
            </label>
            <div className="relative">
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email"
                required
                autoComplete="email"
                className={`
                  w-full px-4 py-3 pl-12
                  bg-dark-secondary/50 
                  border border-dark-tertiary/30 
                  rounded-lg 
                  text-light-primary 
                  placeholder-light-tertiary 
                  backdrop-blur-sm
                  focus:outline-none 
                  focus:ring-2 
                  focus:ring-primary-500/50 
                  focus:border-primary-500
                  transition-all duration-200
                  ${formErrors.email ? 'border-red-500 focus:ring-red-500/50 focus:border-red-500' : ''}
                `}
              />
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-light-tertiary" />
            </div>
            {formErrors.email && (
              <p className="mt-1 text-sm text-red-400">{formErrors.email}</p>
            )}
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-light-secondary mb-2">
              Password
            </label>
            <PasswordInput
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder="Enter your password"
              required
              autoComplete="current-password"
              error={!!formErrors.password}
            />
            {formErrors.password && (
              <p className="mt-1 text-sm text-red-400">{formErrors.password}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="
              w-full px-6 py-3
              bg-gradient-to-r from-primary-600 to-primary-500
              hover:from-primary-700 hover:to-primary-600
              disabled:from-primary-800 disabled:to-primary-700
              disabled:cursor-not-allowed
              text-white font-medium
              rounded-lg
              transition-all duration-200
              focus:outline-none
              focus:ring-2
              focus:ring-primary-500/50
              flex items-center justify-center space-x-2
            "
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Signing In...</span>
              </>
            ) : (
              <span>Sign In</span>
            )}
          </button>
        </form>

        <div className="mt-8 text-center">
          <p className="text-light-tertiary">
            Don't have an account?{' '}
            <button
              type="button"
              onClick={onSwitchToSignUp}
              className="
                text-primary-400 hover:text-primary-300
                font-medium transition-colors duration-200
                focus:outline-none focus:underline
              "
            >
              Create Account
            </button>
          </p>
        </div>
      </div>
    </div>
  )
}